/**
 * The QR module matrix and the geometric predicates that describe it.
 *
 * This is the single source of truth for "where are the finder patterns" and
 * "what counts as the centre". The previous implementation kept one copy here
 * and a second, subtly different copy inside the canvas component; only the
 * component's copy was ever executed.
 */

/** Every finder pattern is a 7x7 block, one in each corner except bottom-right. */
export const FINDER_PATTERN_SIZE = 7;

export interface QrMatrix {
  /** Number of modules along one edge. Always square. */
  readonly size: number;
  /** Bounds-checked module lookup; out-of-range coordinates read as light. */
  isDark(row: number, col: number): boolean;
}

/**
 * Backed by a flat `Uint8Array` rather than `boolean[][]` so that a lookup is a
 * single bounds check and cannot return `undefined` under
 * `noUncheckedIndexedAccess`.
 */
export function createMatrix(
  size: number,
  isDarkAt: (row: number, col: number) => boolean,
): QrMatrix {
  const cells = new Uint8Array(size * size);
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      cells[row * size + col] = isDarkAt(row, col) ? 1 : 0;
    }
  }

  return {
    size,
    isDark(row, col) {
      if (row < 0 || col < 0 || row >= size || col >= size) return false;
      return cells[row * size + col] === 1;
    },
  };
}

/** Top-left corner of each of the three finder patterns, in module coordinates. */
export function finderPatternOrigins(
  size: number,
): readonly (readonly [row: number, col: number])[] {
  const far = size - FINDER_PATTERN_SIZE;
  return [
    [0, 0],
    [0, far],
    [far, 0],
  ];
}

export function isFinderPatternModule(row: number, col: number, size: number): boolean {
  return finderPatternOrigins(size).some(
    ([originRow, originCol]) =>
      row >= originRow &&
      row < originRow + FINDER_PATTERN_SIZE &&
      col >= originCol &&
      col < originCol + FINDER_PATTERN_SIZE,
  );
}

/**
 * The square of modules hidden behind the centre image.
 *
 * `sizePercent` is the image's edge length as a percentage of the whole symbol.
 * The cleared region is derived from the same fractional geometry the image is
 * drawn with, so the hole stays concentric with the image at every module count
 * — the old integer-halving version could sit half a module off centre.
 */
export function centerAreaBounds(
  size: number,
  sizePercent: number,
): { readonly start: number; readonly end: number } {
  const span = size * (sizePercent / 100);
  const start = Math.floor((size - span) / 2);
  const end = Math.ceil((size + span) / 2) - 1;
  return { start, end };
}

export function isCenterAreaModule(
  row: number,
  col: number,
  size: number,
  sizePercent: number,
): boolean {
  const { start, end } = centerAreaBounds(size, sizePercent);
  return row >= start && row <= end && col >= start && col <= end;
}

/**
 * Centre coordinates of the alignment patterns for each QR version (1-40),
 * from ISO/IEC 18004 Annex E. Version 1 has none.
 *
 * This replaces a predicate that reported a single 5x5 block at the matrix
 * centre — which is not where alignment patterns live at any version.
 */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
  [6, 28, 50, 72, 94],
  [6, 26, 50, 74, 98],
  [6, 30, 54, 78, 102],
  [6, 28, 54, 80, 106],
  [6, 32, 58, 84, 110],
  [6, 30, 58, 86, 114],
  [6, 34, 62, 90, 118],
  [6, 26, 50, 74, 98, 122],
  [6, 30, 54, 78, 102, 126],
  [6, 26, 52, 78, 104, 130],
  [6, 30, 56, 82, 108, 134],
  [6, 34, 60, 86, 112, 138],
  [6, 30, 58, 86, 114, 142],
  [6, 34, 62, 90, 118, 146],
  [6, 30, 54, 78, 102, 126, 150],
  [6, 24, 50, 76, 102, 128, 154],
  [6, 28, 54, 80, 106, 132, 158],
  [6, 32, 58, 84, 110, 136, 162],
  [6, 26, 54, 82, 110, 138, 166],
  [6, 30, 58, 86, 114, 142, 170],
];

/** Symbol version derived from the module count: size = 4 * version + 17. */
export function versionForSize(size: number): number {
  return (size - 17) / 4;
}

/** Top-left corners of every alignment pattern, each 5x5 modules. */
export function alignmentPatternOrigins(
  size: number,
): readonly (readonly [row: number, col: number])[] {
  const version = versionForSize(size);
  const centers = ALIGNMENT_CENTERS[version - 1];
  if (centers === undefined || centers.length === 0) return [];

  const first = centers[0];
  const last = centers.at(-1);
  if (first === undefined || last === undefined) return [];

  const origins: (readonly [number, number])[] = [];
  for (const row of centers) {
    for (const col of centers) {
      // The three positions coinciding with finder patterns are skipped.
      const onFinder =
        (row === first && col === first) ||
        (row === first && col === last) ||
        (row === last && col === first);
      if (onFinder) continue;
      origins.push([row - 2, col - 2]);
    }
  }
  return origins;
}

export function isAlignmentPatternModule(row: number, col: number, size: number): boolean {
  return alignmentPatternOrigins(size).some(
    ([originRow, originCol]) =>
      row >= originRow && row < originRow + 5 && col >= originCol && col < originCol + 5,
  );
}

/** The two timing lines, which carry the scanner's module-grid reference. */
export function isTimingPatternModule(row: number, col: number, size: number): boolean {
  if (row !== 6 && col !== 6) return false;
  // Inside a finder pattern the timing rule does not apply.
  return !isFinderPatternModule(row, col, size);
}

/**
 * Function patterns are what a scanner locks onto: finder, timing and
 * alignment. Decorating them is what makes a styled code unreadable, so the
 * renderer keeps them as solid squares regardless of the chosen module style.
 */
export function isFunctionPatternModule(row: number, col: number, size: number): boolean {
  return (
    isFinderPatternModule(row, col, size) ||
    isTimingPatternModule(row, col, size) ||
    isAlignmentPatternModule(row, col, size)
  );
}
